import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Payment } from "../types";
import "../styles/PaymentHistory.css";
import { userPaymentHistory } from "../api/userApi";
import { StudentPaymentResponse } from "../types/user";
import { Receipt, ReceiptItem } from "../types";

// Lazy load PDF libraries using require (webpack compatible)
const loadPDFLibraries = () => {
  try {
    // Use require for webpack compatibility
    // @ts-ignore
    const jsPDFModule = require("jspdf");
    // @ts-ignore
    const html2canvasModule = require("html2canvas");
    
    // For jspdf 2.x, the default export is the jsPDF class
    // Handle both CommonJS and ES module formats
    let jsPDF;
    if (jsPDFModule.default) {
      jsPDF = jsPDFModule.default;
    } else if (jsPDFModule.jsPDF) {
      jsPDF = jsPDFModule.jsPDF;
    } else {
      jsPDF = jsPDFModule;
    }
    
    const html2canvas = html2canvasModule.default || html2canvasModule;
    
    return { jsPDF, html2canvas };
  } catch (error) {
    console.error("Failed to load PDF libraries:", error);
    throw new Error("PDF libraries not available. Please ensure jspdf and html2canvas are installed.");
  }
};

const PaymentHistory: React.FC = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState<
    "all" | "COMPLETED" | "pending" | "failed"
  >("all");

  // Mock payment data - in real app, this would come from API
  const [payments] = useState<Payment[]>([
    {
      id: "1",
      amount: 5000,
      date: "2024-01-15",
      description: "Library Membership Fee",
      status: "COMPLETED",
      transactionId: "TXN001234567",
    },
    {
      id: "2",
      amount: 2000,
      date: "2024-01-10",
      description: "Seat Booking Fee",
      status: "COMPLETED",
      transactionId: "TXN001234568",
    },
    {
      id: "3",
      amount: 1000,
      date: "2024-01-05",
      description: "Monthly Maintenance Fee",
      status: "pending",
      transactionId: "TXN001234569",
    },
    {
      id: "4",
      amount: 3000,
      date: "2023-12-20",
      description: "Library Membership Fee",
      status: "COMPLETED",
      transactionId: "TXN001234570",
    },
    {
      id: "5",
      amount: 1500,
      date: "2023-12-15",
      description: "Late Fee",
      status: "failed",
      transactionId: "TXN001234571",
    },
  ]);
  const paymentHistory = userPaymentHistory(user?.studentId || "");
  const filteredPayments =
    filter === "all" ? payments : payments.filter((p) => p.status === filter);

  const totalAmount = payments
    .filter((p) => p.status === "COMPLETED")
    .reduce((sum, p) => sum + p.amount, 0);

  const getStatusBadge = (status: string) => {
    const statusClass =
      status === "COMPLETED"
        ? "COMPLETED"
        : status === "pending"
        ? "pending"
        : "failed";
    return (
      <span className={`status-badge ${statusClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const ConvertToDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString();
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Generate receipt from payment data
  const generateReceipt = (payment: StudentPaymentResponse): Receipt => {
    const receiptNumber = `RCP-${new Date(payment.paymentDate).getFullYear()}-${String(payment.paymentId).padStart(3, '0')}`;
    const items: ReceiptItem[] = [
      {
        name: "Library Membership Fee",
        qty: 1,
        unit: payment.amount,
      },
    ];

    return {
      id: String(payment.paymentId),
      receiptNumber: receiptNumber,
      paymentId: String(payment.paymentId),
      date: payment.paymentDate,
      amount: payment.amount,
      items: items,
      studentName: user?.name || "Student User",
      studentId: user?.studentId || "",
    };
  };

  // Handle view receipt - opens in new window
  const handleViewReceipt = (payment: StudentPaymentResponse) => {
    const receipt = generateReceipt(payment);
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt - ${receipt.receiptNumber}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                padding: 40px; 
                max-width: 800px; 
                margin: 0 auto;
                background: #f8fafc;
              }
              .receipt-container {
                background: white;
                padding: 40px;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
              .receipt-header { 
                text-align: center; 
                margin-bottom: 30px; 
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 20px;
              }
              .receipt-header h1 {
                margin: 0 0 10px 0;
                color: #1e293b;
                font-size: 28px;
              }
              .receipt-header h2 {
                margin: 0;
                color: #64748b;
                font-size: 18px;
                font-weight: normal;
              }
              .receipt-details { 
                margin-bottom: 30px; 
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
              }
              .receipt-details p {
                margin: 8px 0;
                color: #475569;
              }
              .receipt-details strong {
                color: #1e293b;
              }
              .receipt-items { 
                width: 100%; 
                border-collapse: collapse; 
                margin-bottom: 20px; 
              }
              .receipt-items th, 
              .receipt-items td { 
                padding: 12px; 
                text-align: left; 
                border-bottom: 1px solid #e5e7eb; 
              }
              .receipt-items th {
                background: #f8fafc;
                font-weight: 600;
                color: #1e293b;
                text-transform: uppercase;
                font-size: 12px;
                letter-spacing: 0.5px;
              }
              .receipt-total { 
                text-align: right; 
                font-size: 20px; 
                font-weight: bold; 
                margin-top: 20px; 
                padding-top: 20px;
                border-top: 2px solid #e5e7eb;
                color: #059669;
              }
              @media print {
                body {
                  background: white;
                  padding: 0;
                }
                .receipt-container {
                  box-shadow: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              <div class="receipt-header">
                <h1>Om Sai Reading Room</h1>
                <h2>Payment Receipt</h2>
              </div>
              <div class="receipt-details">
                <p><strong>Receipt Number:</strong> ${receipt.receiptNumber}</p>
                <p><strong>Date:</strong> ${new Date(receipt.date).toLocaleDateString()}</p>
                <p><strong>Student Name:</strong> ${receipt.studentName}</p>
                <p><strong>Student ID:</strong> ${receipt.studentId}</p>
                <p><strong>Payment ID:</strong> ${receipt.paymentId}</p>
                <p><strong>Validity:</strong> ${new Date(payment.validityFrom).toLocaleDateString()} - ${new Date(payment.validityTo).toLocaleDateString()}</p>
              </div>
              <table class="receipt-items">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${receipt.items.map(item => `
                    <tr>
                      <td>${item.name}</td>
                      <td>${item.qty}</td>
                      <td>${formatCurrency(item.unit)}</td>
                      <td>${formatCurrency(item.unit * item.qty)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              <div class="receipt-total">
                <p>Total Amount: ${formatCurrency(receipt.amount)}</p>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  // Handle download receipt - downloads as PDF file
  const handleDownloadReceipt = async (payment: StudentPaymentResponse) => {
    const receipt = generateReceipt(payment);
    
    try {
      // Load PDF libraries
      const { jsPDF: PDF, html2canvas: h2c } = loadPDFLibraries();
      
      // Create a temporary div to render the receipt
      const receiptDiv = document.createElement("div");
      receiptDiv.style.position = "absolute";
      receiptDiv.style.left = "-9999px";
      receiptDiv.style.width = "800px";
      receiptDiv.style.padding = "40px";
      receiptDiv.style.backgroundColor = "#ffffff";
      receiptDiv.style.fontFamily = "Arial, sans-serif";
      receiptDiv.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px;">
          <h1 style="margin: 0 0 10px 0; color: #1e293b; font-size: 28px;">Om Sai Reading Room</h1>
          <h2 style="margin: 0; color: #64748b; font-size: 18px; font-weight: normal;">Payment Receipt</h2>
        </div>
        <div style="margin-bottom: 30px; display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Receipt Number:</strong> ${receipt.receiptNumber}</p>
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Date:</strong> ${new Date(receipt.date).toLocaleDateString()}</p>
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Student Name:</strong> ${receipt.studentName}</p>
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Student ID:</strong> ${receipt.studentId}</p>
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Payment ID:</strong> ${receipt.paymentId}</p>
          <p style="margin: 8px 0; color: #475569;"><strong style="color: #1e293b;">Validity:</strong> ${new Date(payment.validityFrom).toLocaleDateString()} - ${new Date(payment.validityTo).toLocaleDateString()}</p>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="background: #f8fafc;">
              <th style="padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #1e293b; text-transform: uppercase; font-size: 12px;">Item</th>
              <th style="padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #1e293b; text-transform: uppercase; font-size: 12px;">Quantity</th>
              <th style="padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #1e293b; text-transform: uppercase; font-size: 12px;">Unit Price</th>
              <th style="padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; font-weight: 600; color: #1e293b; text-transform: uppercase; font-size: 12px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${receipt.items.map(item => `
              <tr>
                <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #475569;">${item.name}</td>
                <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #475569;">${item.qty}</td>
                <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #475569;">${formatCurrency(item.unit)}</td>
                <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #475569;">${formatCurrency(item.unit * item.qty)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="text-align: right; font-size: 20px; font-weight: bold; margin-top: 20px; padding-top: 20px; border-top: 2px solid #e5e7eb; color: #059669;">
          <p style="margin: 0;">Total Amount: ${formatCurrency(receipt.amount)}</p>
        </div>
      `;
      
      document.body.appendChild(receiptDiv);
      
      // Wait a bit for the DOM to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Convert to canvas
      const canvas = await h2c(receiptDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      // Create PDF
      const imgData = canvas.toDataURL("image/png");
      // For jspdf 2.x, the default export is the jsPDF class
      const pdf = new PDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`receipt-${receipt.receiptNumber}.pdf`);
      document.body.removeChild(receiptDiv);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };
  const [paymentHistoryData, setPaymentHistoryData] = useState<
    StudentPaymentResponse[]
  >([]);

  useEffect(() => {
    fetch();
  }, []);

  const fetch = async () => {
    const response = await userPaymentHistory(user?.studentId || "");
    setPaymentHistoryData(response?.data || []);
  };
  return (
    <div className="payment-history">
      <div className="payment-header">
        <h2>Payment History</h2>
        <p>View all your payment transactions</p>
      </div>

      <div className="payment-summary">
        <div className="summary-card">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <label>Total Paid</label>
            <div className="summary-amount">
              {formatCurrency(
                paymentHistoryData.reduce((sum, x) => sum + x.amount, 0)
              )}
            </div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <label>Total Transactions</label>
            <div className="summary-count">{paymentHistoryData.length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <label>Completed</label>
            <div className="summary-count">
              {
                paymentHistoryData.filter((p) => p.status === "COMPLETED")
                  .length
              }
            </div>
          </div>
        </div>
      </div>

      <div className="payment-filters">
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          className={`filter-btn ${filter === "COMPLETED" ? "active" : ""}`}
          onClick={() => setFilter("COMPLETED")}
        >
          Completed
        </button>
        <button
          className={`filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pending
        </button>
        <button
          className={`filter-btn ${filter === "failed" ? "active" : ""}`}
          onClick={() => setFilter("failed")}
        >
          Failed
        </button>
      </div>

      <div className="payments-list">
        {filteredPayments.length === 0 ? (
          <div className="no-payments">
            <p>No payments found</p>
          </div>
        ) : (
          <div className="payments-table-container">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Days</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistoryData.map((payment) => (
                  <tr key={payment.paymentId}>
                    <td>
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </td>
                    <td className="amount-cell">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td>{getStatusBadge(payment.status)}</td>
                    <td>{`${ConvertToDate(
                      payment.validityFrom
                    )}-${ConvertToDate(payment.validityTo)}`}</td>
                    <td>
                      {Math.ceil(
                        (new Date(payment.validityTo).getTime() -
                          new Date(payment.validityFrom).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )}
                    </td>
                    <td>
                      {payment.status === "COMPLETED" && (
                        <div className="action-buttons">
                          <button
                            className="action-btn btn-primary"
                            onClick={() => handleViewReceipt(payment)}
                            title="View Receipt"
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>View Receipt</span>
                          </button>
                          <button
                            className="action-btn download-btn"
                            onClick={() => handleDownloadReceipt(payment)}
                            title="Download Receipt as PDF"
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span>Download PDF</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
